import { useEffect, useState, useCallback } from "react";
import { useDataStore } from "@/lib/store/data-store";
import { DeckDraftClientEngine, type DeckDraftState, type UnitPhaseState } from "@/lib/classes/deck-drafter/DeckDraftClientEngine";
import type { Unit } from "@/lib/types/unit";
import { Deck } from "@/lib/classes/deck";
import { DivisionPicker } from "@/components/DeckDrafter/DivisionPicker";
import { UnitPicker } from "@/components/DeckDrafter/UnitPicker";
import { DeckView } from "@/components/DeckBuilder/DeckView";
import { CompletionScreen } from "@/components/DeckDrafter/CompletionScreen";
import { Loader2, LayoutGrid } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

function DraftEscapeToolbar({
  onRestart,
  onFinishEarly,
  finishEarlyDisabled,
  buttonsDisabled,
}: {
  onRestart: () => void | Promise<void>;
  onFinishEarly: () => void | Promise<void>;
  finishEarlyDisabled: boolean;
  buttonsDisabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={buttonsDisabled}
        onClick={() => void onRestart()}
      >
        Restart draft
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={buttonsDisabled || finishEarlyDisabled}
        onClick={() => void onFinishEarly()}
      >
        Finish with current deck
      </Button>
    </div>
  );
}

export function DeckDrafter() {
  const { units, divisions, loadingUnits, loadingDivisions } = useDataStore();
  const [unitMap, setUnitMap] = useState<{ [key: string]: Unit }>({});
  const [engine, setEngine] = useState<DeckDraftClientEngine | null>(null);
  const [state, setState] = useState<DeckDraftState | null>(null);
  const [activeDeck, setActiveDeck] = useState<Deck | null>(null);
  const [disableButtons, setDisableButtons] = useState(false);
  const [deckPreviewOpen, setDeckPreviewOpen] = useState(false);

  // Create unit map
  useEffect(() => {
    if (units) {
      const map: { [key: string]: Unit } = {};
      for (const unit of units) {
        map[unit.descriptorName] = unit;
      }
      setUnitMap(map);
    }
  }, [units]);

  // Initialize engine
  useEffect(() => {
    if (divisions && divisions.length > 0 && units && units.length > 0) {
      const newEngine = new DeckDraftClientEngine();
      
      // Try to restore from localStorage first
      newEngine.getStateFromLocalStorage().then((savedState) => {
        // If state is COMPLETE, start a new draft instead of restoring
        if (savedState && savedState.phase !== "COMPLETE") {
          newEngine.state = savedState;
          newEngine.units = units;
          newEngine.divisions = divisions;
          const map: { [key: string]: Unit } = {};
          for (const unit of units) {
            map[unit.descriptorName] = unit;
          }
          newEngine.unitMap = map;
          setState(savedState);
        } else {
          // Initialize new draft (either no saved state or state is COMPLETE)
          newEngine.initializeDraft(units, divisions).then(() => {
            setState(newEngine.state || null);
          });
        }
      });

      // Register callback that uses refs to always get latest values
      newEngine.callbacks.push(() => {
        if (newEngine.state) {
          setState(newEngine.state);
        }
      });

      setEngine(newEngine);
    }
  }, [divisions, units]);

  // Update deck whenever state or unitMap changes
  useEffect(() => {
    if (!state || Object.keys(unitMap).length === 0 || !divisions || divisions.length === 0) {
      return;
    }

    if (state.phase === "UNIT_PICK" && state.data.deckCode !== undefined) {
      if (state.data.deckCode !== "") {
        try {
          const deck = Deck.fromDeckCode(state.data.deckCode, {
            unitMap,
            divisions,
          });
          setActiveDeck(deck);
        } catch (error) {
          console.error("[DeckDrafter] Failed to create deck from code:", error);
          setActiveDeck(null);
        }
      } else {
        // Empty deck code - create new deck for the division
        const division = divisions.find(
          (div) => div.descriptor === state.data.division
        );
        if (division) {
          const deck = new Deck({
            unitMap,
            division,
          });
          setActiveDeck(deck);
        }
      }
    } else if (state.phase === "COMPLETE") {
      try {
        const deck = Deck.fromDeckCode(state.data.deckCode, {
          unitMap,
          divisions,
        });
        setActiveDeck(deck);
      } catch (error) {
        console.error("[DeckDrafter] Failed to create deck from code:", error);
        setActiveDeck(null);
      }
    } else {
      setActiveDeck(null);
    }
  }, [state, unitMap, divisions]);

  const chooseOption = useCallback(async (choice: number) => {
    if (!engine) return;

    try {
      setDisableButtons(true);
      await engine.chooseOption(choice);
      engine.fireCallbacks();
    } catch (error) {
      console.error("Error choosing option:", error);
    } finally {
      setDisableButtons(false);
    }
  }, [engine]);

  const restartDraft = useCallback(async () => {
    if (!engine) return;

    try {
      setDisableButtons(true);
      await engine.restartDraft();
      engine.fireCallbacks();
    } catch (error) {
      console.error("Error restarting draft:", error);
    } finally {
      setDisableButtons(false);
    }
  }, [engine]);

  const finishDraftEarly = useCallback(async () => {
    if (!engine) return;

    try {
      setDisableButtons(true);
      await engine.finishDraftEarly();
      engine.fireCallbacks();
    } catch (error) {
      console.error("Error finishing draft early:", error);
    } finally {
      setDisableButtons(false);
    }
  }, [engine]);

  if (loadingDivisions || loadingUnits || !engine || !state) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-purple-300">Loading deck drafter...</p>
        </div>
      </div>
    );
  }

  // Render based on phase
  if (state.phase === "DIVISION_PICK") {
    return (
      <div className="min-h-screen">
        <DivisionPicker
          choices={state.data.choices}
          divisions={divisions || []}
          onDivisionChosen={chooseOption}
          disabled={disableButtons}
        />
      </div>
    );
  }

  if (state.phase === "UNIT_PICK") {
    const unitPickState = state as UnitPhaseState;
    const draftDivision = divisions?.find(
      (div) => div.descriptor === unitPickState.data.division
    );
    const activationIncomplete =
      !!activeDeck &&
      !!draftDivision &&
      activeDeck.totalSpentActivationPoints < draftDivision.maxActivationPoints;

    if (state.data.choices.length === 0) {
      // No more choices available - show completion
      return (
        <div className="h-full flex overflow-hidden">
          {/* Desktop sidebar */}
          <div className="hidden lg:flex lg:w-[360px] xl:w-[400px] 2xl:w-[450px] lg:flex-shrink-0 border-r flex flex-col overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Deck</h3>
            </div>
            <div className="flex-1 overflow-hidden p-4 min-h-0">
              {activeDeck ? (
                <DeckView deck={activeDeck} onRemoveUnit={undefined} onUpdateUnit={undefined} />
              ) : (
                <div className="text-sm text-muted-foreground">No deck yet</div>
              )}
            </div>
          </div>

          {/* Mobile deck preview button */}
          <div className="lg:hidden fixed bottom-4 left-4 z-40">
            <Button
              onClick={() => setDeckPreviewOpen(true)}
              size="lg"
              className="rounded-full shadow-lg h-12 w-12"
              aria-label="View deck"
            >
              <LayoutGrid className="h-5 w-5" />
            </Button>
          </div>

          {/* Mobile deck preview sheet */}
          <Sheet open={deckPreviewOpen} onOpenChange={setDeckPreviewOpen}>
            <SheetContent
              side="left"
              className="w-[90vw] sm:w-[400px] p-0 overflow-y-auto lg:hidden"
              showCloseButton={true}
            >
              <SheetHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b sticky top-0 bg-background z-10">
                <SheetTitle className="text-lg sm:text-xl font-semibold">Deck</SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-4 min-h-0">
                {activeDeck ? (
                  <DeckView deck={activeDeck} onRemoveUnit={undefined} onUpdateUnit={undefined} />
                ) : (
                  <div className="text-sm text-muted-foreground">No deck yet</div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
            <div className="container mx-auto py-2 flex flex-col gap-3">
              <DraftEscapeToolbar
                onRestart={restartDraft}
                onFinishEarly={finishDraftEarly}
                finishEarlyDisabled={unitPickState.data.deckCode === ""}
                buttonsDisabled={disableButtons}
              />
              {activationIncomplete ? (
                <p className="text-sm text-muted-foreground border border-border rounded-md p-3 bg-muted/30">
                  No further picks are available before spending all activation points.
                  Use Finish with current deck to export what you have, or Restart draft to begin again.
                </p>
              ) : null}
              <CompletionScreen
                deckCode={state.data.deckCode}
                deck={activeDeck}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex overflow-hidden">
        {/* Desktop sidebar */}
        <div className="hidden lg:flex lg:w-[360px] xl:w-[400px] 2xl:w-[450px] lg:flex-shrink-0 border-r flex flex-col overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Deck</h3>
          </div>
          <div className="flex-1 overflow-hidden p-4 min-h-0">
            {activeDeck ? (
              <DeckView deck={activeDeck} onRemoveUnit={undefined} onUpdateUnit={undefined} />
            ) : (
              <div className="text-sm text-muted-foreground">No deck yet</div>
            )}
          </div>
        </div>
        
        {/* Mobile deck preview button */}
        <div className="lg:hidden fixed bottom-4 left-4 z-40">
          <Button
            onClick={() => setDeckPreviewOpen(true)}
            size="lg"
            className="rounded-full shadow-lg h-12 w-12"
            aria-label="View deck"
          >
            <LayoutGrid className="h-5 w-5" />
          </Button>
        </div>

        {/* Mobile deck preview sheet */}
        <Sheet open={deckPreviewOpen} onOpenChange={setDeckPreviewOpen}>
          <SheetContent
            side="left"
            className="w-[90vw] sm:w-[400px] p-0 overflow-y-auto lg:hidden"
            showCloseButton={true}
          >
            <SheetHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b sticky top-0 bg-background z-10">
              <SheetTitle className="text-lg sm:text-xl font-semibold">Deck</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-4 min-h-0">
              {activeDeck ? (
                <DeckView deck={activeDeck} onRemoveUnit={undefined} onUpdateUnit={undefined} />
              ) : (
                <div className="text-sm text-muted-foreground">No deck yet</div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
          <div className="container mx-auto px-4 py-2 flex flex-col gap-3">
            <DraftEscapeToolbar
              onRestart={restartDraft}
              onFinishEarly={finishDraftEarly}
              finishEarlyDisabled={unitPickState.data.deckCode === ""}
              buttonsDisabled={disableButtons}
            />
            <UnitPicker
              choices={state.data.choices}
              units={units || []}
              deck={activeDeck}
              onUnitChosen={chooseOption}
              disabled={disableButtons}
            />
          </div>
        </div>
      </div>
    );
  }

  if (state.phase === "COMPLETE") {
    return (
      <div className="min-h-screen">
        <CompletionScreen
          deckCode={state.data.deckCode}
          deck={activeDeck}
        />
      </div>
    );
  }

  return null;
}

